<template>
  <div
    v-if="isOpen"
    class="modal-overlay modal-overlay-dialog modal-overlay-light"
    @click.self="close"
  >
    <div class="modal-content">
      <div class="modal-header">
        <h2>Upgrade to Premium</h2>
        <button
          class="close-button"
          title="Close"
          @click="close"
        >
          ✕
        </button>
      </div>

      <div class="modal-body">
        <p class="description">
          Unlock unlimited playlists and songs with a premium subscription!
        </p>

        <div
          v-if="loadingPlans"
          class="loading-message"
        >
          Loading plans...
        </div>

        <div
          v-else-if="plans.length === 0"
          class="error-message"
        >
          No plans available. Please try again later.
        </div>

        <div
          v-else
          class="grid-responsive"
        >
          <PlanCard
            v-for="plan in plans"
            :key="plan.priceId"
            :plan="plan"
            :is-processing="processingPriceId === plan.priceId"
            :disabled="!auth.isAuthenticated.value"
            compact
            @subscribe="handleSubscribe"
          >
            <template #disabled-message>
              Please log in to subscribe
            </template>
          </PlanCard>
        </div>

        <div
          v-if="error"
          class="error-message"
        >
          {{ error }}
        </div>
      </div>

      <div class="modal-footer">
        <button
          class="btn-outline"
          :disabled="processingPriceId !== null"
          @click="close"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useAuth } from '../composables/useAuth';
import { useSubscriptionPlans } from '../composables/useSubscriptionPlans';
import PlanCard from './PlanCard.vue';

interface Props {
  isOpen: boolean;
  /** If true, show only recurring subscriptions. If false, show all plans including one-time. */
  subscriptionsOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  subscriptionsOnly: false,
});

const emit = defineEmits<{
  close: [];
}>();

const auth = useAuth();
const { plans, processingPriceId, loadingPlans, error, loadPlans, createCheckoutSession } = useSubscriptionPlans();

function close(): void {
  if (!processingPriceId.value) {
    emit('close');
  }
}

async function handleSubscribe(priceId: string): Promise<void> {
  // Auth guard: require authentication before checkout
  if (!auth.isAuthenticated.value) {
    sessionStorage.setItem('pendingPriceId', priceId);
    sessionStorage.setItem('pendingCheckoutSource', 'upgrade-dialog');

    // Close dialog before redirecting to login
    emit('close');
    await auth.login();
    return;
  }

  await createCheckoutSession(priceId);
}

onMounted(async () => {
  // Load plans based on dialog usage:
  // - subscriptionsOnly=true: For conversions from 6-month to subscriptions
  // - subscriptionsOnly=false: For renewals/upgrades, show all purchase options
  await loadPlans(props.subscriptionsOnly);

  // Handle pending purchase from upgrade dialog
  const pendingPriceId = sessionStorage.getItem('pendingPriceId');
  const pendingSource = sessionStorage.getItem('pendingCheckoutSource');

  if (pendingPriceId && pendingSource === 'upgrade-dialog' && auth.isAuthenticated.value) {
    sessionStorage.removeItem('pendingPriceId');
    sessionStorage.removeItem('pendingCheckoutSource');
    await createCheckoutSession(pendingPriceId);
  }
});
</script>

<style scoped>
/* Modal overlay now uses global .modal-overlay utility class */
.modal-overlay {
  animation: fadeIn var(--transition-normal);
}

/* Animations now use global @keyframes fadeIn and slideUp from utilities.css */

/* Modal content now uses global .modal-content utility class with custom animation */
.modal-content {
  animation: slideUp var(--transition-slow);
}

/* Modal structure uses global utilities with custom overrides */
.modal-header {
  padding: var(--spacing-2xl) var(--spacing-3xl);
  border-bottom: 1px solid var(--color-border);
}

.modal-header h2 {
  font-size: var(--font-size-2xl);
}

/* Close button now uses global .close-button utility class */

.modal-body {
  padding: var(--spacing-3xl);
}

.description {
  text-align: center;
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-3xl);
}

.loading-message {
  text-align: center;
  padding: var(--spacing-4xl);
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

/* Plans grid now uses global .grid-responsive utility */

/* Error message now uses global .error-message utility class */

/* Modal footer uses global utility with custom padding and border color */
.modal-footer {
  gap: var(--spacing-md);
  padding: var(--spacing-2xl) var(--spacing-3xl);
  border-top: 1px solid var(--color-border);
  justify-content: center;
}

/* Cancel button now uses global .btn-outline utility */
</style>
